import { useAppDispatch, useAppSelector } from 'modules/hooks/appHooks';
import { UserCredentials } from 'modules/types/UserCredentials';
import {
  BodyText,
  ButtonType,
  Flex,
  HeadlineTitle,
  InputWithLabel,
  Loader,
} from 'modules/ui';
import { FormButtons, FormContainer, FormPane } from 'modules/ui/form';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { MaxFieldLength } from '../../../../ui/input/common/MaxFieldLength';
import { LoginSelectors, LoginThunks } from '../../login/redux';
import { resetError } from '../../login/redux/slice';
import { FooterButtons } from './FooterButtons';
import { NoMarginBodyText, QrImage, StyledTextLink } from './styled';
import { ToastType } from '../../../../ui/toast';
import { useToastContainer } from '../../../../layout/toastContainer';
import { NotifyActionType } from '../../notifyActions/components/TfaNotifyActionSent';
import { useSidePopout } from '../../../../layout';

interface TfaFormProps {
  qrCode: string;
}

export const TfaForm: React.FC<TfaFormProps> = ({ qrCode }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { addToast } = useToastContainer();
  const { closePopout } = useSidePopout();

  const error = useAppSelector(LoginSelectors.selectTfaVerifyError);
  const tfaVerifyLoading = useAppSelector(LoginSelectors.selectUserLoading);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isButtonDisabledForResetTfa, setIsButtonDisabledForResetTfa] =
    useState(false);

  const handleLogin = useCallback(
    (event) => {
      event.preventDefault();
      const formData = new FormData(event.target);
      const inputObject = Object.fromEntries(formData) as UserCredentials; // convert the FormData object to a JSON object
      inputObject?.authCode &&
        dispatch(LoginThunks.tfaVerifyCode(inputObject)).then((res) => {
          res.type === 'auth/tfaVerifyCode/fulfilled' && history.push('/');
        });
    },
    [dispatch, history],
  );

  useEffect(
    () => () => {
      dispatch(resetError());
    },
    [dispatch],
  );

  const redirectToQRNotifyActionSent = () => {
    closePopout();
    history.push({
      pathname: '/tfa-reset-notify-action',
      state: { type: NotifyActionType.QR_RESET },
    });
  };

  const handleQRCodeReset = () => {
    dispatch(LoginThunks.qrCodeResetRequest()).then((res) => {
      if (res.type === 'auth/qrCodeResetRequest/fulfilled') {
        redirectToQRNotifyActionSent();
      } else {
        addToast({
          title: t('landingPage.tfaForm.errorWithResetQrCodeTitle'),
          text: t('landingPage.tfaForm.errorWithResetQrCodeDescription'),
          type: ToastType.Urgent,
        });
      }
    });
  };

  const resetTfaProviderDispatch = () => {
    dispatch(LoginThunks.resetTfaProvider()).then((res) => {
      if (res.type === 'auth/resetTfaProvider/fulfilled') {
        redirectToTfyNotifyActionSent();
      } else {
        addToast({
          title: t(
            'landingPage.tfaChoiceEmail.errorWithOnlyResetTfaProviderTitle',
          ),
          text: t(
            'landingPage.tfaChoiceEmail.errorWithOnlyResetTfaProviderDescription',
          ),
          type: ToastType.Urgent,
        });
      }
    });
  };

  const redirectToTfyNotifyActionSent = () => {
    closePopout();
    history.push({
      pathname: '/tfa-reset-notify-action',
      state: { type: NotifyActionType.TFA_PROVIDER_RESET },
    });
  };

  const handleResetQrCode = () => {
    setIsButtonDisabled(true);
    handleQRCodeReset();
    setTimeout(() => setIsButtonDisabled(false), 30000);
  };

  const handleResetTfaProvider = () => {
    setIsButtonDisabledForResetTfa(true);
    resetTfaProviderDispatch();
    setTimeout(() => setIsButtonDisabledForResetTfa(false), 30000);
  };

  return (
    <Loader active={tfaVerifyLoading}>
      <Flex>
        <HeadlineTitle>{t('landingPage.tfaForm.title')}</HeadlineTitle>
        <FormContainer onSubmit={handleLogin}>
          <FormPane>
            {qrCode ? (
              <>
                <NoMarginBodyText>
                  {t('landingPage.tfaForm.addAuthentificatorText1')}
                </NoMarginBodyText>
                <QrImage src={qrCode} alt="qrCode" />
                <BodyText>
                  {t('landingPage.tfaForm.addAuthentificatorText2')}
                </BodyText>
              </>
            ) : (
              <BodyText>
                {t('landingPage.tfaForm.introTextForAuthGoogleScreen')}
              </BodyText>
            )}
            {!qrCode && (
              <NoMarginBodyText>
                {t('landingPage.tfaForm.repeatTextForAuthGoogleScreen')}
                <StyledTextLink
                  type="button"
                  buttonType={ButtonType.Text}
                  disabled={isButtonDisabled}
                  onClick={() => handleResetQrCode()}>
                  {t('landingPage.tfaForm.resetQrTextLink')}
                </StyledTextLink>
                {t('landingPage.tfaChoiceEmail.newLine')}
                {t('landingPage.tfaForm.switchTextForAuthGoogleScreen')}
                <StyledTextLink
                  type="button"
                  buttonType={ButtonType.Text}
                  disabled={isButtonDisabledForResetTfa}
                  onClick={() => handleResetTfaProvider()}>
                  {t('landingPage.tfaChoiceEmail.resetTfaProviderTextLink')}
                </StyledTextLink>
              </NoMarginBodyText>
            )}
            <InputWithLabel
              name="authCode"
              label={t('landingPage.tfaForm.verifyCode')}
              placeholderText={t('landingPage.tfaForm.verifyCodePlaceholder')}
              maxLength={MaxFieldLength.tfaCode}
              errorText={error ? t('landingPage.tfaForm.error') : undefined}
              autoComplete="off"
              autoFocus
            />
          </FormPane>
          <FormButtons text={t('landingPage.tfaForm.ctaButton')} centered />
          <FooterButtons />
        </FormContainer>
      </Flex>
    </Loader>
  );
};
